import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import Select from 'react-select'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'

import TextAreaField from '../../../../FormLegacy/FormFieldsV2/TextAreaField'
import ToggleField from '../../../../FormLegacy/FormFieldsV2/ToggleField'
import Stepper from '../../../../Stepper/Stepper'
import { useSnackbar } from '../../../../../helpers/hooks/useSnackbar'
import { createMaintenanceRequest } from '../../../../../reducers/maintenanceReducer'
import ImagesPicker from '../../../../ImagesPicker/ImagesPicker'
import ScheduledDateTimeFields from '../../../ScheduledDateTimeFields/ScheduledDateTimeFields'
import { useScheduledDateTime } from '../../../ScheduledDateTimeFields/useScheduledDateTime'
import { getCustomMaintenanceCategories } from '../../../maintenance-categories'

const INITIAL_FORM_STATE = {
  category: null,
  description: '',
  pets: false,
  permissionToEnter: true,
  photos: [],
}

const getDefaultCategoryOptions = (t) => [
  {
    value: 'Plumbing',
    label: t('maintenance:form.native.categoryOptions.plumbing'),
  },
  {
    value: 'Lighting',
    label: t('maintenance:form.native.categoryOptions.lighting'),
  },
  {
    value: 'HVAC',
    label: t('maintenance:form.native.categoryOptions.hvac'),
  },
  {
    value: 'Windows',
    label: t('maintenance:form.native.categoryOptions.windows'),
  },
  {
    value: 'Doors',
    label: t('maintenance:form.native.categoryOptions.doors'),
  },
  {
    value: 'Appliances',
    label: t('maintenance:form.native.categoryOptions.appliances'),
  },
  {
    value: 'Other',
    label: t('maintenance:form.native.categoryOptions.other'),
  },
]

const getCategoryOptions = (t, buildingId) => {
  const customCategories = getCustomMaintenanceCategories(buildingId)
  if (customCategories) {
    return customCategories.map((category) => ({ value: category, label: category }))
  }
  return getDefaultCategoryOptions(t)
}

const NativeSettings = ({
  building,
  resident,
  assignee,
  onBack,
  onCancel,
  createMaintenanceRequest,
  voicemailChatMessageSid,
  inspectionId,
  description,
  defaultPhotos,
  checklistRoomItemId,
  onSuccess,
}) => {
  const { t } = useTranslation(['maintenance', 'common'])
  const scheduledDateTime = useScheduledDateTime({ timezone: building.timezone })
  const { displaySnackbar } = useSnackbar()
  const [formState, setFormState] = useState(INITIAL_FORM_STATE)
  const categoryOptions = getCategoryOptions(t, building.id)

  const handleFieldChange = ({ target: { value, name } }) => {
    setFormState((prev) => ({ ...prev, [name]: value }))
  }

  const handleToggleChange = ({ target: { name, checked } }) => {
    setFormState((prev) => ({ ...prev, [name]: checked }))
  }

  const handleSubmit = async () => {
    if (!scheduledDateTime.validate()) return

    const requestData = {
      buildingId: building.id,
      description: formState.description,
      enterPermission: formState.permissionToEnter,
      pets: formState.pets,
      photos: formState.photos.map((photo, index) =>
        photo.file instanceof File
          ? photo.file
          : new File([photo.file], `photo-${index}.jpg`, { type: photo.file.type || 'image/jpeg' }),
      ),
      ...(formState.category && { category: formState.category.value }),
      ...(assignee && { assignee: assignee.id }),
      ...(scheduledDateTime.timestamp && { scheduledDate: scheduledDateTime.timestamp }),
    }
    if (resident.clientId) {
      requestData.clientId = resident.clientId
    } else if (resident.unit) {
      requestData.unit = resident.unit
    }
    if (resident.sourceUnitId) {
      requestData.unitId = resident.sourceUnitId
    }
    if (voicemailChatMessageSid) {
      requestData.voicemailChatMessageSid = voicemailChatMessageSid
    }
    if (checklistRoomItemId) {
      requestData.checklistRoomItemId = checklistRoomItemId
    } else if (inspectionId) {
      requestData.inspectionId = inspectionId
    }

    try {
      await createMaintenanceRequest(building.id, requestData)
      displaySnackbar(t('maintenance:form.pmsSettings.snackbar.created'))
      if (onSuccess) {
        onSuccess()
      }
    } catch {
      displaySnackbar(t('maintenance:form.pmsSettings.snackbar.error'), true)
    } finally {
      onCancel()
    }
  }

  const areAllFieldsValid = !!formState.description

  useEffect(() => {
    if (description) {
      setFormState((prev) => ({ ...prev, description }))
    }
  }, [description])

  // Note: Load default photos if these are passed
  useEffect(() => {
    if (!defaultPhotos || !defaultPhotos.length) return

    const loadDefaultPhotos = async () => {
      try {
        const preparedPhotos = await Promise.all(
          defaultPhotos.slice(0, 3).map(async (url, index) => {
            try {
              const response = await fetch(url)
              const blob = await response.blob()
              const file = new File([blob], `photo-${index}.jpg`, {
                type: blob.type || 'image/jpeg',
              })

              return {
                image: url,
                file,
              }
            } catch {
              return null
            }
          }),
        )

        const validPhotos = preparedPhotos.filter(Boolean)
        if (validPhotos.length) {
          setFormState((prev) => ({
            ...prev,
            photos: validPhotos,
          }))
        }
      } catch (error) {
        console.error(error)
      }
    }

    loadDefaultPhotos()
  }, [defaultPhotos])

  return (
    <div className="maintenance-request-modal">
      <Stepper
        steps={[
          t('maintenance:form.steps.requestDetails'),
          t('maintenance:form.steps.requestSettings'),
        ]}
        currentStep={1}
      />
      <div className="row">
        <div className="row-header">{t('maintenance:form.pmsSettings.selectCategory')}</div>
        <Select
          className="select-container"
          classNamePrefix="select-primary"
          options={categoryOptions}
          name="category"
          value={formState.category}
          onChange={(e) => setFormState((prev) => ({ ...prev, category: e }))}
          placeholder={t('maintenance:form.pmsSettings.selectCategory')}
        />
      </div>
      <div className="row">
        <ScheduledDateTimeFields {...scheduledDateTime.fieldProps} />
      </div>
      <div className="row">
        <div className="row-header">{t('maintenance:form.pmsSettings.requestDescription')}</div>
        <TextAreaField
          placeholder={t('maintenance:form.pmsSettings.descriptionPlaceholder')}
          name="description"
          value={formState.description}
          maxLength={250}
          onChange={handleFieldChange}
        />
      </div>
      <div className="row">
        <div className="row-header">
          {t('maintenance:form.pmsSettings.photosLabel')}{' '}
          <span>{t('maintenance:form.requestDetails.optional')}</span>
        </div>
        <ImagesPicker
          value={formState.photos}
          imageLimit={3}
          onChange={(images) => handleFieldChange({ target: { value: images, name: 'photos' } })}
        />
      </div>
      <div className="row checkboxes">
        <div className="input-container">
          <ToggleField
            value={formState.pets}
            name="pets"
            label={t('maintenance:form.pmsSettings.pets')}
            labelOnRight
            onChange={handleToggleChange}
          />
        </div>
        <div className="input-container">
          <ToggleField
            name="permissionToEnter"
            label={t('maintenance:form.pmsSettings.permissionToEnter')}
            labelOnRight
            onChange={handleToggleChange}
            value={formState.permissionToEnter}
          />
        </div>
      </div>
      <div className="modal-footer">
        <div className="buttons">
          <button className="button button-gray" onClick={onBack}>
            {t('maintenance:form.pmsSettings.back')}
          </button>
          <button className="button button-gray" onClick={onCancel}>
            {t('common:actions.cancel')}
          </button>
          <button
            className="button button-primary"
            disabled={!areAllFieldsValid}
            onClick={handleSubmit}
          >
            {t('maintenance:form.pmsSettings.createRequest')}
          </button>
        </div>
      </div>
    </div>
  )
}

NativeSettings.propTypes = {
  createMaintenanceRequest: PropTypes.func,
  building: PropTypes.object,
  resident: PropTypes.object,
  assignee: PropTypes.object,
  onBack: PropTypes.func,
  onCancel: PropTypes.func,
  voicemailChatMessageSid: PropTypes.bool,
  inspectionId: PropTypes.number,
  description: PropTypes.string,
  defaultPhotos: PropTypes.array,
  checklistRoomItemId: PropTypes.number,
  onSuccess: PropTypes.func,
}

export default connect(undefined, {
  createMaintenanceRequest,
})(NativeSettings)
